import { Badge, Button, Input } from '@artosera/ui';
import { USAGES_IMAGE, usageAutorise, type DroitsImage } from '@artosera/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MediaDeLOeuvre } from '../../../api/oeuvres.js';
import { useModifierDroitsImage } from '../../../api/medias.js';
import { estApiError } from '../../../api/errors.js';

export function DroitsImagePanneau({
  galerieSlug,
  media,
  peutEcrire,
}: {
  galerieSlug: string;
  media: MediaDeLOeuvre;
  peutEcrire: boolean;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const tr = (cle: string) => t(`regie:droitsImage.${cle}`);
  const [droits, setDroits] = useState<DroitsImage>(
    media.droits ?? {
      titulaire: '',
      usages: [],
      echeance: null,
      note: '',
    },
  );
  const modifier = useModifierDroitsImage(galerieSlug);
  const maintenant = new Date();
  const echeance = media.droits?.echeance;
  const proche = echeance && new Date(echeance).getTime() <= maintenant.getTime() + 30 * 86400000;
  return (
    <details>
      <summary>
        {tr('titre')} {media.droits == null && <Badge>{tr('manquants')}</Badge>}
        {proche && (
          <Badge tone="reserved">
            {tr(new Date(echeance).getTime() <= maintenant.getTime() ? 'expires' : 'bientot')}
          </Badge>
        )}
        {!usageAutorise(media.droits, 'site', maintenant) && (
          <Badge tone="reserved">{tr('siteInterdit')}</Badge>
        )}
      </summary>
      {peutEcrire ? (
        <div>
          <label>
            {tr('titulaire')}
            <Input
              maxLength={200}
              value={droits.titulaire}
              onChange={(e) => setDroits({ ...droits, titulaire: e.target.value })}
            />
          </label>
          <fieldset>
            <legend>{tr('usagesTitre')}</legend>
            {USAGES_IMAGE.map((usage) => (
              <label key={usage}>
                <input
                  type="checkbox"
                  checked={droits.usages.includes(usage)}
                  onChange={(e) =>
                    setDroits({
                      ...droits,
                      usages: e.target.checked
                        ? [...droits.usages, usage]
                        : droits.usages.filter((u) => u !== usage),
                    })
                  }
                />
                {tr(`usages.${usage}`)}
              </label>
            ))}
          </fieldset>
          <label>
            {tr('echeance')}
            <Input
              type="date"
              value={droits.echeance?.slice(0, 10) ?? ''}
              onChange={(e) =>
                setDroits({
                  ...droits,
                  echeance: e.target.value ? `${e.target.value}T00:00:00.000Z` : null,
                })
              }
            />
          </label>
          <label>
            {tr('note')}
            <Input
              maxLength={300}
              value={droits.note}
              onChange={(e) => setDroits({ ...droits, note: e.target.value })}
            />
          </label>
          <Button
            disabled={!droits.titulaire.trim() || modifier.isPending}
            onClick={() => modifier.mutate({ id: media.id, droits })}
          >
            {t('commun:actions.enregistrer')}
          </Button>
          <Button
            variant="ghost"
            disabled={modifier.isPending}
            onClick={() => modifier.mutate({ id: media.id, droits: null })}
          >
            {tr('effacer')}
          </Button>
          {modifier.isError && (
            <p role="alert">
              {estApiError(modifier.error) ? modifier.error.message : t('commun:erreurReseau')}
            </p>
          )}
        </div>
      ) : (
        <p>
          {media.droits?.titulaire} ·{' '}
          {media.droits?.usages.map((u) => tr(`usages.${u}`)).join(', ')} {echeance?.slice(0, 10)}
        </p>
      )}
    </details>
  );
}
